import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDealerPricingRequestAggregations } from "../../api/quote/getDealerPricingRequestAggregations";
import { getQuote } from "../../api/quote/getQuote";
import { searchDealerPricingRequests } from "../../api/quote/searchDealerPricingRequests";
import { getVehicleMakes } from "../../api/vehicle/getVehicleMakes";
import { getVehicleModels } from "../../api/vehicle/getVehicleModels";
import { getVehicleModelYears } from "../../api/vehicle/getVehicleModelYears";
import { getVehicleVariants } from "../../api/vehicle/getVehicleVariants";
import APSelectDropDown from "../../components/APSelectDropDown";
import APButton from "../../components/buttons/APButton";
import BackButton from "../../components/buttons/BackButton";
import EmptyList from "../../components/EmptyList";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import APDateField from "../../components/form/APDateField";
import APForm from "../../components/form/APForm";
import APTextField from "../../components/form/APTextField";
import LoadingBackdrop from "../../components/LoadingBackdrop";
import PageContainer from "../../components/PageContainer";
import PaginationContainer from "../../components/PaginationContainer";
import { buildAutoCompleteOptions } from "../../helpers/buildOptions";
import { getCRMClientURL } from "../../helpers/configuration";
import { currentDate, endOfDay, now, subtractDate } from "../../helpers/dates";
import {
  getAutoCompleteOptionFromParam,
  getStringFilterFromParam,
} from "../../helpers/search";
import {
  buildVehiclePricingRequestSearchParams,
  buildVehiclePricingRequestSearchQueryFromParams,
  VehiclePricingRequestSearchInputOptions,
  VehiclePricingRequestSearchSortOption,
  VehiclePricingRequestSearchSortOptionsList,
} from "../../helpers/vehiclePricingRequests";
import { useCascadeReset } from "../../hooks/useCascadeReset";
import { stateOptions } from "../../types/State";
import {
  pricingSelectedOptions,
  VehiclePricingRequestSearchFilters,
} from "../../types/VehiclePricingRequest";
import PricingRequestAverages from "../requestVehiclePricing/components/PricingRequestAverages";
import PricingRequestList from "../requestVehiclePricing/components/PricingRequestList";

export const DEFAULT_PAGE_SIZE = 12;

export type VehiclePricingRequestSearchInputs = {
  vehicleMake: AutoCompleteOption | null;
  vehicleModel: AutoCompleteOption | null;
  vehicleYear: AutoCompleteOption | null;
  vehicleVariant: AutoCompleteOption | null;
  pricingReceivedDateFrom: string;
  maxOnroadPrice: string;
  dealerState: AutoCompleteOption | null;
  pricingSelected: AutoCompleteOption | null;
};

const SearchVehiclePricingRequests = () => {
  const {
    watch,
    control,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<VehiclePricingRequestSearchInputs>({ mode: "onTouched" });
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationHistory, setPaginationHistory] = useState<string[]>([]);
  const [filters, setFilters] = useState<VehiclePricingRequestSearchFilters>(
    buildVehiclePricingRequestSearchQueryFromParams(
      searchParams,
      DEFAULT_PAGE_SIZE
    )
  );
  const quoteId = searchParams.get("quoteId") || undefined;
  const hasSetQueryParams = useRef(false);
  const hasOverrides =
    !!searchParams.get("vehicleMake") ||
    !!searchParams.get("vehicleModel") ||
    !!searchParams.get("vehicleYear") ||
    !!searchParams.get("vehicleVariant") ||
    !!searchParams.get("pricingReceivedDate") ||
    !!searchParams.get("maxOnroadPrice") ||
    !!searchParams.get("dealerState") ||
    !!searchParams.get("pricingSelected");

  const {
    data: dealerPricingRequests,
    isFetching: isDealerPricingRequestsFetching,
    isLoading: isDealerPricingRequestsLoading,
    isSuccess: isDealerPricingRequestsSuccess,
  } = searchDealerPricingRequests(filters, {
    keepPreviousData: true,
    disabled: !hasSetQueryParams.current,
  });
  const {
    data: dealerPricingRequestAggregations,
    isFetching: isDealerPricingRequestAggregationsFetching,
    isLoading: isDealerPricingRequestAggregationsLoading,
    isSuccess: isDealerPricingRequestAggregationsSuccess,
  } = getDealerPricingRequestAggregations(filters, {
    keepPreviousData: true,
    disabled: !hasSetQueryParams.current,
  });

  const quote = getQuote(quoteId || "", {
    disabled: !quoteId,
  });
  const makes = getVehicleMakes();
  const config = getPublicConfig();
  const crmClientURL = config.isSuccess ? getCRMClientURL(config.data) : null;

  if (makes.isError) {
    throw makes.error;
  }

  const updateSearchParams = (
    inputs: VehiclePricingRequestSearchInputs,
    sort?: VehiclePricingRequestSearchSortOption
  ) => {
    const params = buildVehiclePricingRequestSearchParams(inputs);
    if (quoteId) {
      params.set("quoteId", quoteId);
    }

    if (sort) {
      params.set("sort", sort);
    }

    setSearchParams(params, { replace: true });

    return params;
  };

  const onSubmit = (data: VehiclePricingRequestSearchInputs) => {
    updateSearchParams(
      data,
      searchParams.get("sort") as VehiclePricingRequestSearchSortOption
    );
  };

  const onSortChange = (optionId: VehiclePricingRequestSearchSortOption) => {
    searchParams.set("sort", optionId);
    setSearchParams(searchParams);
  };
  const handlePageChange = (
    nextPageLink: string | undefined,
    paginationHistory: string[]
  ) => {
    setPaginationHistory(paginationHistory);
    setFilters({ ...filters, nextPageLink });
  };

  const watchVehicleMake = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleMake",
    childField: "vehicleModel",
  });
  const watchVehicleModel = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleModel",
    childField: "vehicleYear",
  });
  const watchVehicleYear = useCascadeReset({
    watch,
    setValue,
    parentField: "vehicleYear",
    childField: "vehicleVariant",
  });

  // Retrieve vehicle model years and variants
  const models = getVehicleModels(watchVehicleMake?.id || null, {
    disabled: !watchVehicleMake,
  });
  const modelYears = getVehicleModelYears(watchVehicleModel?.id || null, {
    disabled: !watchVehicleModel,
  });

  const variants = getVehicleVariants(
    watchVehicleMake?.id || null,
    watchVehicleModel?.id || null,
    watchVehicleYear?.id || null,
    {
      disabled: !watchVehicleMake || !watchVehicleModel || !watchVehicleYear,
    }
  );

  const vehiclePricingRequestSearchInputOptions: VehiclePricingRequestSearchInputOptions =
    {
      vehicleMakes: makes.isSuccess
        ? buildAutoCompleteOptions(makes.data, "name", "id")
        : [],
      vehicleModels: models.isSuccess
        ? buildAutoCompleteOptions(models.data, "name", "id")
        : [],
      vehicleYears: modelYears.isSuccess
        ? buildAutoCompleteOptions(
            modelYears.data.map((year) => {
              return { id: `${year}`, name: `${year}` };
            }),
            "name",
            "id"
          )
        : [],
      vehicleVariants: variants.isSuccess
        ? buildAutoCompleteOptions(variants.data, "name", "id")
        : [],
      state: stateOptions,
      pricingSelected: pricingSelectedOptions,
    };

  // Handle query parameters for all actions after data is loaded
  useEffect(() => {
    // Only handle query parameters if we have vehicle overrides and haven't set them yet
    const shouldHandleQueryParams = hasOverrides && !hasSetQueryParams.current;

    if (shouldHandleQueryParams) {
      let hasSetAnyValue = false;
      const queryMake = searchParams.get("vehicleMake");
      const queryModel = searchParams.get("vehicleModel");
      const queryYear = searchParams.get("vehicleYear");
      const queryVariant = searchParams.get("vehicleVariant");
      const queryPricingReceivedDateFrom = searchParams.get(
        "pricingReceivedDate"
      );
      const queryMaxOnroadPrice = searchParams.get("maxOnroadPrice");
      const queryState = searchParams.get("dealerState");
      const queryPricingSelected = searchParams.get("pricingSelected");

      // Handle make from query parameters
      if (queryMake && makes.isSuccess && makes.data) {
        const makeOption = getAutoCompleteOptionFromParam(
          searchParams,
          "vehicleMake",
          vehiclePricingRequestSearchInputOptions.vehicleMakes
        );
        if (makeOption) {
          setValue("vehicleMake", makeOption);
          hasSetAnyValue = true;
        }
      }

      // Handle model from query parameters (after make is set)
      if (queryModel && models.isSuccess && models.data) {
        const modelOption = getAutoCompleteOptionFromParam(
          searchParams,
          "vehicleModel",
          vehiclePricingRequestSearchInputOptions.vehicleModels
        );
        if (modelOption) {
          setValue("vehicleModel", modelOption);
          hasSetAnyValue = true;
        }
      }

      // Handle year from query parameters
      if (queryYear && modelYears.isSuccess && modelYears.data) {
        const yearOption = getAutoCompleteOptionFromParam(
          searchParams,
          "vehicleYear",
          vehiclePricingRequestSearchInputOptions.vehicleYears
        );
        if (yearOption) {
          setValue("vehicleYear", yearOption);
          hasSetAnyValue = true;
        }
        hasSetAnyValue = true;
      }

      // Handle variant from query parameters (after year is set)
      if (queryVariant && variants.isSuccess && variants.data) {
        const variantOption = getAutoCompleteOptionFromParam(
          searchParams,
          "vehicleVariant",
          vehiclePricingRequestSearchInputOptions.vehicleVariants
        );
        if (variantOption) {
          setValue("vehicleVariant", variantOption);
          hasSetAnyValue = true;
        }
      }

      // Handle pricing received date from from query parameters
      if (queryPricingReceivedDateFrom) {
        setValue(
          "pricingReceivedDateFrom",
          getStringFilterFromParam(searchParams, "pricingReceivedDate") || ""
        );
        hasSetAnyValue = true;
      }

      // Handle max onroad price from query parameters
      if (queryMaxOnroadPrice) {
        setValue(
          "maxOnroadPrice",
          getStringFilterFromParam(searchParams, "onroadPrice") || ""
        );
        hasSetAnyValue = true;
      }

      // Handle state from query parameters
      if (queryState) {
        const stateOption = getAutoCompleteOptionFromParam(
          searchParams,
          "dealerState",
          stateOptions
        );
        if (stateOption) {
          setValue("dealerState", stateOption);
          hasSetAnyValue = true;
        }
      }

      // Handle pricing selected from query parameters
      if (queryPricingSelected) {
        setValue(
          "pricingSelected",
          getAutoCompleteOptionFromParam(
            searchParams,
            "pricingSelected",
            pricingSelectedOptions
          )
        );
        hasSetAnyValue = true;
      }

      // Only mark as complete if we've processed all available query parameters
      const hasAllRequiredData =
        (!queryMake || makes.isSuccess) &&
        (!queryModel || models.isSuccess) &&
        (!queryYear || modelYears.isSuccess) &&
        (!queryVariant || variants.isSuccess);

      if (hasAllRequiredData && hasSetAnyValue) {
        hasSetQueryParams.current = true;
      }
    } else if (!hasSetQueryParams.current) {
      const pricingReceivedDateFrom = dayjs(
        subtractDate(currentDate(), 6, "month")
      ).toISOString();
      const pricingSelected =
        pricingSelectedOptions.find((option) => option.id === "all") || null;

      updateSearchParams(
        {
          vehicleMake: null,
          vehicleModel: null,
          vehicleYear: null,
          vehicleVariant: null,
          maxOnroadPrice: "",
          dealerState: null,
          pricingReceivedDateFrom,
          pricingSelected,
        },
        "onroadPriceAsc"
      );

      setValue("pricingReceivedDateFrom", pricingReceivedDateFrom);
      setValue("pricingSelected", pricingSelected);

      hasSetQueryParams.current = true;
    }
  }, [
    hasOverrides,
    hasSetQueryParams.current,
    makes.isSuccess,
    models.isSuccess,
    modelYears.isSuccess,
    variants.isSuccess,
  ]);

  // Scroll to top of page when filters change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [filters]);

  // Update filters when query parameters change
  useEffect(() => {
    setPaginationHistory([]);
    setFilters(
      buildVehiclePricingRequestSearchQueryFromParams(
        searchParams,
        DEFAULT_PAGE_SIZE
      )
    );
  }, [searchParams]);

  const isLoading =
    makes.isFetching ||
    quote.isLoading ||
    config.isLoading ||
    isDealerPricingRequestsLoading ||
    isDealerPricingRequestAggregationsLoading;
  const isSuccess =
    makes.isSuccess &&
    config.isSuccess &&
    (!quoteId || quote.isSuccess) &&
    isDealerPricingRequestsSuccess &&
    isDealerPricingRequestAggregationsSuccess;

  return (
    <PageContainer
      title="Vehicle Pricing Received"
      loading={isLoading}
      size="full"
      titleButton={
        <Box
          sx={{
            width: {
              xs: "168px",
              sm: "190px",
            },
            display: "flex",
          }}
        >
          <APSelectDropDown
            label="Sort By"
            selectedOptionId={
              searchParams.get("sort") as VehiclePricingRequestSearchSortOption
            }
            options={VehiclePricingRequestSearchSortOptionsList}
            onOptionChange={onSortChange}
          />
        </Box>
      }
    >
      {isSuccess && (
        <Stack spacing={2}>
          <APForm
            onSubmit={handleSubmit(onSubmit)}
            submitText="Search"
            isLoading={isLoading}
            isError={Object.keys(errors).length > 0}
            minSpacing
            hideSubmitButton
          >
            <Stack
              spacing={0}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { md: "1fr 1fr 1fr" },
              }}
            >
              <Stack spacing={2}>
                <APAutoComplete
                  name="vehicleMake"
                  label="Vehicle Make"
                  options={vehiclePricingRequestSearchInputOptions.vehicleMakes}
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={null}
                />

                <APAutoComplete
                  name="vehicleModel"
                  label="Vehicle Model"
                  options={
                    vehiclePricingRequestSearchInputOptions.vehicleModels
                  }
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={null}
                  disabled={!watchVehicleMake || !models.isSuccess}
                />

                <APAutoComplete
                  name="vehicleYear"
                  label="Vehicle Year"
                  options={vehiclePricingRequestSearchInputOptions.vehicleYears}
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={null}
                  disabled={
                    !watchVehicleMake ||
                    !watchVehicleModel ||
                    !modelYears.isSuccess
                  }
                />
              </Stack>
              <Stack spacing={2}>
                <APAutoComplete
                  name="vehicleVariant"
                  label="Vehicle Variant"
                  options={
                    vehiclePricingRequestSearchInputOptions.vehicleVariants
                  }
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={null}
                  disabled={
                    !watchVehicleMake ||
                    !watchVehicleModel ||
                    !watchVehicleYear ||
                    !variants.isSuccess
                  }
                />

                <APDateField
                  name="pricingReceivedDateFrom"
                  label="Pricing Received After"
                  control={control}
                  defaultValue={null}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                  maxDate={endOfDay(now())}
                  clearable
                />

                <APTextField
                  name="maxOnroadPrice"
                  label="Max Onroad"
                  control={control}
                  validations={{
                    formatValidation: /^[0-9]+$/,
                  }}
                  errors={errors}
                  defaultValue={""}
                  startAdornment="$"
                  placeholder="xxxxxxx"
                  formatNumber={true}
                />
              </Stack>
              <Stack spacing={2}>
                <APAutoComplete
                  name="dealerState"
                  label="State"
                  options={stateOptions}
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={null}
                />

                <APAutoComplete
                  name="pricingSelected"
                  label="Pricing Selected"
                  options={pricingSelectedOptions}
                  control={control}
                  validations={{}}
                  errors={errors}
                  defaultValue={null}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    {dealerPricingRequestAggregations && (
                      <PricingRequestAverages
                        aggregations={dealerPricingRequestAggregations}
                      />
                    )}
                  </Box>

                  <Box mt={1}>
                    <APButton
                      variant="contained"
                      text="Search"
                      color="primary"
                      onClick={handleSubmit(onSubmit)}
                    />
                  </Box>
                </Box>
              </Stack>
            </Stack>
          </APForm>

          {dealerPricingRequests && (
            <>
              {dealerPricingRequests.items.length > 0 ? (
                <PaginationContainer
                  paginationHistory={paginationHistory}
                  currentPage={paginationHistory.length + 1}
                  pageSize={DEFAULT_PAGE_SIZE}
                  itemCount={dealerPricingRequests.count || 0}
                  onPageChange={handlePageChange}
                  nextPageLink={dealerPricingRequests.nextPageLink || ""}
                  isLoading={isDealerPricingRequestsFetching}
                >
                  <PricingRequestList
                    displayType="list"
                    cardType="generic"
                    requests={dealerPricingRequests.items}
                    type="received"
                    crmClientURL={crmClientURL}
                    columns="triple"
                  />
                </PaginationContainer>
              ) : (
                <EmptyList message="No pricing requests found" />
              )}
              {(isDealerPricingRequestsFetching ||
                isDealerPricingRequestAggregationsFetching) && (
                <LoadingBackdrop />
              )}
            </>
          )}

          <Box>
            <BackButton />
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default SearchVehiclePricingRequests;
